import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Tippy from '@tippyjs/react';

import { updateERA } from 'api/ERA';
import { getPatients } from 'api/Patients';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import useModal from 'lib/hooks/useModal';

import { showAlert } from 'components/shared/Alert/Alert';
import Icon from 'components/shared/Icon/Icon';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Select from 'components/shared/Select/Select';

const PatientService = ({ data }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [patient, setPatient] = useState({ id: null, label: null });
  const { isOpen, openModal, closeModal } = useModal();

  const searchPatients = async (searchTerm) => {
    if (searchTerm) {
      try {
        const result = await getPatients(navigate, { searchTerm });

        if (result?.patients) {
          return result.patients.map((patient) => ({ value: patient.id, label: patient.fullName }));
        }
      } catch (error) {
        showAlert({
          title: 'Error',
          message: 'There was an error getting patients.',
          color: 'danger'
        });
      }
    }
  };

  const mutateUpdateERA = useCustomMutation({
    invalidateQueryKey: ['eras', 'era'],
    successMessage: 'Patient updated successfully!',
    errorMessage: 'Failed to update patient!',
    mutationFn: ({ eraId, fields }) => updateERA(navigate, { eraId, fields })
  });

  const selectPatient = (e) => {
    setPatient({ id: e?.value, label: e?.label });
    openModal();
  };

  const handleUpdatePatient = () => {
    mutateUpdateERA.mutate({
      eraId: id,
      fields: { unmatched_claims: true, index: data?.index, pcn: patient?.id }
    });
  };

  const ConfirmMessage = () => {
    return (
      <>
        <p>{`Are you sure you want to add ${patient?.label} to this Claim`}</p>
        <p className="text-danger-500">This action cannot be reverted.</p>
      </>
    );
  };

  if (data?.isClaimRow) {
    return (
      <>
        <Tippy
          content={data.patientName ? 'View Chart' : null}
          placement="top"
          className="tippy-dark"
          disabled={!data.patientName}>
          {data.patientName ? (
            <span
              className="cursor-pointer hover:underline"
              onClick={() => {
                if (data.patientName) {
                  window.open(`/portal/charts/${data.pcn}/billing`, '_blank');
                }
              }}>
              {data.patientName}
            </span>
          ) : (
            <div className="my-1 flex h-full items-center gap-1">
              <Select
                isAsync
                loadOptions={searchPatients}
                onChange={selectPatient}
                styles={{ control: { maxHeight: '40px', minWidth: '200px', maxWidth: '300px' } }}
                menuPortalTarget={document.body}
                noOptionsMessage={() => 'Start typing the name.'}
              />
              <Tippy content={`User ID: ${data?.pcn}`} placement="top" className="tippy-dark">
                <div>
                  <Icon icon="info-circle-linear" size={24} />
                </div>
              </Tippy>
            </div>
          )}
        </Tippy>
        <Confirm
          isOpen={isOpen}
          handleContinue={() => handleUpdatePatient()}
          handleClose={() => {
            setPatient({ id: null, label: null });
            closeModal();
          }}
          icon="patient-add-mono"
          title="Add Patient to Claim?"
          message={<ConfirmMessage />}
          primaryBtnTxt="Confirm"
        />
      </>
    );
  }
  return <span className="text-primary-700">{data?.procedure_code || ''}</span>;
};

export default PatientService;
